import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMenu, FiBell, FiSearch, FiSettings, FiHelpCircle, FiHome } from 'react-icons/fi';
import { useAuth,  } from '../../context/AuthContext';
import { get } from '../../utils/apiUtils'; // Import the API utility
import { getCompanyData, setUserData } from '../../utils/authUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCircleUser, faUser } from '@fortawesome/free-regular-svg-icons';
import { faArrowUpRightFromSquare, faCircleMinus, faHome, faHomeAlt, faHomeLgAlt, faPlusCircle, faPowerOff, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { upadateCompany } from '../../services/authService';
import { useToast } from '../common/ToastProvider';
import { useRefresh } from '../../context/RefreshContext';
import { downloadImage } from '../../services/fileUploadService';
import { useMsal } from '@azure/msal-react';
import { getCompaniesByFilter } from '../../services/companyService';



const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: #ffffff;
  // border-bottom: 1px solid var(--border-color);
  height: 50px;
  position: sticky;
  top: 0;
  z-index: 100;
  // box-shadow: 0 2px 4px rgba(0, 102, 255, 0.05);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  margin-right: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
    color: #0066FF;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: #ffffff;
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #0066FF;
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  font-size: 1rem;
`;

const CreateClaimButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem .5rem;
  margin-left: 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  margin-left: 0.5rem;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
    color: #0066FF;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--danger);
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
  }
`;

const Avatar = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 3px;
  background: #0066FF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  margin-right: 0.75rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const UserInfo = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Dropdown = styled(motion.div)`
  position: absolute;
  top: 70px;
  right: 14px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 102, 255, 0.1);
  border: 1px solid rgba(0, 102, 255, 0.1);
  width: 220px;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  padding: 0.8rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 102, 255, 0.05);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 102, 255, 0.05);
  }
`;

const DropdownIcon = styled.div`
  font-size: 1.1rem;
  color: var(--text-secondary);
  font-family: 'Orbitron', sans-serif;
`;

const DropdownText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
`;

const Submenu = styled.div`
  padding: 0.4rem 0.8rem;
  background: #ffffff;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
`;


const Logo = styled.img`
  height: 30px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const Header = ({ toggleSidebar, isSidebarOpen }) => {
  const navigate = useNavigate(); 
  const { refresh } = useRefresh();
  const { logout, user, setUser, setProfileUpdated, profileUpdated } = useAuth(); // Assuming setUser is available in AuthContext
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCompanies, setShowCompanies] = useState(false);
  const [companyData, setCompanyData] = useState({});
  const [hasNotifications, setHasNotifications] = useState(true);
  const [profileImage, setProfileImage] = useState(null);

  // Fetch user data on component load
  const companies = getCompanyData();
  const { showToast } = useToast();
  const location = useLocation();
    const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await get(`/api/v1/mms_authentication/me`,{});
        setUser(response); // Update user context with fetched data
        setUserData(response)
        setProfileUpdated(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // navigate('/login'); // Redirect to login if unauthorized
      }
    };

    fetchUserData();
  }, [navigate, setUser, profileUpdated]);

  useEffect(() => {
      if (user && user.companyCd) {
        const fetchCompany = async () => {
          try {
            const filter = { 
              code: user.companyCd
            };
            const response = await getCompaniesByFilter(filter);
            if (response.length) {
              const company = response[0];
              setCompanyData({
                id: company.id || '',
                name: company.name || '',
                status: company.status || '',
                description: company.description || '',
                logo_url: company.logo_url || '',
                currency: company.currency || '',
                date_format: company.date_format || '',
                timezone: company.timezone || ''
              });
            }
          } catch (err) {
            console.error("Error fetching company:", err);
          }
        };
  
        fetchCompany();
      }
    }, [user]);

  useEffect(() => {
    showUserDropdown && setShowUserDropdown(false);
  },[location])

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (user?.profile_url) {
        try {
          // const profile = await get(`${BASE_URL}/api/v1/mms_persistence/download${user.profile_url}`, {
          //   withCredentials: true,
          //   responseType: 'blob',
          // });
          
          const profile = await downloadImage(`${user.profile_url}`)
          const imageUrl = URL.createObjectURL(profile);
          setProfileImage(imageUrl);
        } catch (error) {
          console.error('Error fetching profile image:', error);
        }
      }
    };

    fetchProfileImage();
  }, [user?.profile_url]);

  const handleLogout = async () => {
    setShowUserDropdown(false);
    if (accounts.length > 0) {
      instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    } else {
      await logout();
      navigate('/login');
    }
  };


  const handleCompanyChange = async (companyData) => {
    try {
      const res = await upadateCompany(companyData);
      if (res) {
        showToast({
          message: "Company changed successfully.",
          type: "success"
        });
        setShowUserDropdown(false);
        setShowCompanies(false);
        setUser(res);
        refresh()
      }
    } catch (error) {
      console.log("Error while changing the company.");
    }
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.firstname) return 'U';
    return [user.firstname, user.lastname].map(n => n[0]).join('').toUpperCase();
  };

  return (
    <HeaderContainer>
      <LeftSection>
        {/* <MenuButton onClick={toggleSidebar}>
          <FiMenu />
        </MenuButton> */}
        <Logo 
          onClick={() => navigate('/listing')}
          src='/images/logo/meclaims-icon.png' 
          alt="Logo"
        />
        {/* {companyData.logo_url && (
          <Logo 
            src={companyData.logo_url} 
            alt="Company Logo"
            style={{ paddingLeft: '1rem',marginLeft: '1rem', marginRight: '1rem', height: '40px', borderLeft: '1px solid rgba(0, 102, 255, 0.1)' }}
          />
        )} */}
        {/* <SearchContainer>
          <SearchInput placeholder="Search..." />
          <SearchIcon />
        </SearchContainer> */}
        
      </LeftSection>

      <RightSection>
        {/* <IconButton>
          <FiBell />
          {hasNotifications && <NotificationBadge />}
        </IconButton> */}
          {/* <CreateClaimButton onClick={() => navigate('/claim-create')}>
          <FontAwesomeIcon icon={faPlusCircle} />
          Create Claim
        </CreateClaimButton> */}
        <UserProfile className='user-menu' onClick={() => setShowUserDropdown(!showUserDropdown)}>
          <Avatar>
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              getUserInitials()
            )}
          </Avatar>
          <UserInfo>
            <UserName>{user?.firstname} {user?.lastname}</UserName>
            <UserRole>{user?.company_name || 'NA'}</UserRole>
          </UserInfo>
        </UserProfile>

        {showUserDropdown && (
          <Dropdown
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <DropdownItem onClick={() => navigate('/settings')}>
              <DropdownIcon><FontAwesomeIcon icon={faCircleUser} /></DropdownIcon>
              <DropdownText>Profile</DropdownText>
            </DropdownItem>
            <DropdownItem 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} 
                onClick={() => setShowCompanies(prev => !prev)}
              >
              <DropdownIcon><FontAwesomeIcon icon={faBuilding} /></DropdownIcon>

              <DropdownText>Company</DropdownText>
              {!showCompanies && <DropdownIcon style={{ marginLeft: 'auto' }}>
                <FontAwesomeIcon size="sm" icon={faPlusCircle} />
              </DropdownIcon>}
              {showCompanies && <DropdownIcon style={{ marginLeft: 'auto' }}>
                <FontAwesomeIcon size="sm" icon={faCircleMinus} />
              </DropdownIcon>}
            </DropdownItem>
            <Submenu isVisible={showCompanies}>
              {Array.isArray(companies) && companies.length > 0 ? (
                companies.map((company) => (
                  <DropdownItem key={company.company_cd} onClick={() => handleCompanyChange(company)}>
                    <DropdownText>{company.company_name}</DropdownText>
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem>
                  <DropdownText>No companies available</DropdownText>
                </DropdownItem>
              )}
            </Submenu>
            <DropdownItem onClick={handleLogout}>
              <DropdownIcon><FontAwesomeIcon icon={faPowerOff} /></DropdownIcon>
              <DropdownText>Logout</DropdownText>
            </DropdownItem>
          </Dropdown>
        )}
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;
